import { Button, Grid, InputLabel, TextField, Typography } from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  ApiError,
  ApiErrorClass,
  ApiResponse,
  Plan,
} from "@readyfit-common/models";
import { handleApiError } from "@readyfit-common/utils";
import { LoadingButton } from "src/grading/components";
import RFModal from "src/grading/components/RFModal/RFModal";
import { useAppDispatch } from "src/store/hooks";
import { showSnackbar } from "src/store/reducers/navigation/navigationSlice";
import {
  UpdatePlanTemplatesLibraryRequest,
  useDeletePlanTemplatesLibraryMutation,
  useSaveToPlanTemplatesLibraryMutation,
  useUpdatePlanTemplatesLibraryMutation,
} from "src/store/reducers/training/trainingApiSlice";
import theme from "src/theme";
import useStyles from "./PlanTemplatesModal.style";
import "react-time-picker/dist/TimePicker.css";
import "react-clock/dist/Clock.css";

interface PlanTemplateModalProps {
  open: boolean;
  onClose: () => void;
  isEditing?: boolean;
  planTemplate: Plan | null;
}

const validationSchema = Yup.object().shape({
  name: Yup.string().required("Name is required"),
  planDescription: Yup.string().nullable(),
});

interface PlanTemplateFormValues {
  name: string;
  planDescription?: string;
}

const PlanTemplateModal = ({
  open,
  onClose,
  isEditing,
  planTemplate,
}: PlanTemplateModalProps) => {
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const [
    saveToPlanTemplatesLibrary,
    { isLoading: saveToPlanTemplatesLibraryLoading },
  ] = useSaveToPlanTemplatesLibraryMutation();
  const [
    updatePlanTemplatesLibrary,
    { isLoading: updatePlanTemplatesLibraryLoading },
  ] = useUpdatePlanTemplatesLibraryMutation();
  const [
    deletePlanTemplatesLibrary,
    { isLoading: deletePlanTemplatesLibraryLoading },
  ] = useDeletePlanTemplatesLibraryMutation();

  const initialValues: PlanTemplateFormValues = {
    name: planTemplate?.name || "",
    planDescription: planTemplate?.planDescription || "",
  };

  const formik = useFormik({
    initialValues,
    validationSchema,
    validateOnChange: false,
    enableReinitialize: true,
    onSubmit: async (values: PlanTemplateFormValues) => {
      let response: ApiResponse<void> | null = null;
      console.log("here");
      try {
        // EDIT WORKOUT
        if (isEditing) {
          if (!planTemplate?.id) {
            throw new ApiErrorClass({
              message: "No workout id",
            });
          }
          const shouldIncludeName = planTemplate?.name !== values.name;
          const payload: UpdatePlanTemplatesLibraryRequest = {
            ...(shouldIncludeName ? { name: values.name } : {}),
            planDescription: values.planDescription,
            id: planTemplate.id,
          };
          response = await updatePlanTemplatesLibrary(payload).unwrap();
        } else {
          // ADD WORKOUT
          response = await saveToPlanTemplatesLibrary(values).unwrap();
        }
        if (!response?.success)
          throw new Error(response?.message || "Error saving plan template");
        dispatch(
          showSnackbar({
            open: true,
            message: "Plan template saved successfully",
          }),
        );
        handleClose();
      } catch (error: unknown) {
        const typedError = error as ApiErrorClass;
        console.log("error", error);
        dispatch(
          showSnackbar({
            open: true,
            message: typedError?.data?.message || "Error saving plan template",
            type: "error",
          }),
        );
      }
    },
  });

  const handleDelete = async () => {
    try {
      if (!planTemplate?.id) {
        throw new ApiErrorClass({
          message: "Plan Template ID is required",
        });
      }
      await deletePlanTemplatesLibrary(planTemplate.id).unwrap();
      dispatch(
        showSnackbar({
          open: true,
          message: "Plan Template deleted successfully",
        }),
      );
      handleClose();
    } catch (error: unknown) {
      const typedError = error as ApiError;
      handleApiError(dispatch, typedError);
    }
  };

  const { values, handleSubmit, errors, handleChange, resetForm } = formik;

  const handleClose = () => {
    resetForm();
    onClose();
  };

  return (
    <RFModal
      open={open}
      boxProps={{ className: classes.container }}
      onClose={handleClose}
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <Grid
        container
        component="form"
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          handleSubmit();
        }}
        rowGap={theme.spacing(3)}
      >
        <Typography variant="h5" fontSize={18}>
          {isEditing ? "Edit Plan Template" : "Add Plan Template"}
        </Typography>

        <Grid
          container
          component="form"
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleSubmit();
          }}
          rowGap={theme.spacing(3)}
          spacing={2}
        >
          <Grid item xs={12}>
            <InputLabel htmlFor="name" className={classes.inputLabel}>
              <Typography variant="subtitle1">Name</Typography>
            </InputLabel>
            <TextField
              fullWidth
              variant="outlined"
              value={values.name}
              placeholder="Name"
              InputProps={{
                className: classes.input,
              }}
              onChange={handleChange("name")}
              error={!!errors.name}
              helperText={errors.name}
            />
          </Grid>

          <Grid item xs={12}>
            <InputLabel
              htmlFor="planDescription"
              className={classes.inputLabel}
            >
              Description
            </InputLabel>
            <TextField
              id="planDescription"
              name="planDescription"
              multiline
              rows={2}
              fullWidth
              variant="outlined"
              value={values.planDescription}
              placeholder="Description"
              error={!!errors.planDescription}
              helperText={errors.planDescription}
              InputProps={{
                className: classes.input,
              }}
              onChange={handleChange("planDescription")}
            />
          </Grid>
        </Grid>

        <Grid
          container
          item
          xs={12}
          justifyContent="space-between"
          marginTop={theme.spacing(4)}
        >
          <Grid item>
            <Button
              className={` ${classes.button} ${classes.deleteButton}`}
              onClick={() => {
                if (isEditing) {
                  handleDelete();
                } else {
                  onClose();
                }
              }}
              variant="contained"
            >
              {isEditing ? "Delete" : "Cancel"}
            </Button>
          </Grid>

          <Grid item>
            <LoadingButton
              variant="contained"
              color="primary"
              className={classes.button}
              label="Save"
              onClick={() => formik.handleSubmit()}
              loading={
                saveToPlanTemplatesLibraryLoading ||
                deletePlanTemplatesLibraryLoading ||
                updatePlanTemplatesLibraryLoading
              }
            />
          </Grid>
        </Grid>
      </Grid>
    </RFModal>
  );
};

export default PlanTemplateModal;
